import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import { Perm, type Warehouse } from '../api/types'
import { Banner, Button, ErrorBanner, Field, Panel, Spinner, TextInput } from '@shop/ui'

/**
 * المستودعات — <b>تعطيلٌ وتفعيل، ولا حذف</b>.
 *
 * <p>
 * ⚠ <b>والمعطَّلُ يختفي من قوائم الاختيار ويبقى في الدفاتر.</b> مستودعٌ حُذف
 * يترك كلَّ حركةٍ أشارت إليه بلا مكان، وجردَ السنةِ الماضية بلا معنى.
 * </p>
 *
 * <p>
 * ⚠ <b>و«بضاعةٌ في الطريق» ليس مكانًا يختاره أحد.</b> النظامُ يكتب فيه بنفسه
 * حين يُشحن تحويلٌ بين فرعين ويُخرجه حين يُستلَم، ولا يُمنح لموظّفٍ أصلًا
 * (<c>user_warehouses_not_transit</c>). <b>فتعطيلُه يكسر التحويلَ يومًا لا
 * ينتبه فيه أحدٌ إلى السبب</b> — ولذلك يُعرض هنا ولا يُعطَّل.
 * </p>
 */

/** ما لا يُعطَّل — <b>وسببُه مكتوبٌ بجانبه</b>. */
const SYSTEM_KINDS: Record<string, string> = {
  TRANSIT: 'يكتب فيه النظامُ عند شحن التحويلات — وتعطيلُه يكسرها',
}

const KIND_NAMES: Record<string, string> = {
  MAIN: 'الرئيسيّ',
  SHELF: 'رفُّ العرض',
  REPAIR: 'الصيانة',
  DAMAGED: 'التالف',
  TRANSIT: 'عبور',
}

export default function Warehouses() {
  const { can } = useAuth()

  const warehouses = useQuery({
    queryKey: ['warehouses', 'admin'],
    queryFn: () => api<Warehouse[]>('/api/v1/warehouses?includeArchived=true'),
    enabled: can(Perm.warehouseView),
  })

  const mayEdit = can(Perm.warehouseEdit)
  const rows = warehouses.data ?? []
  const active = rows.filter((w) => w.isActive && w.kind !== 'TRANSIT')

  return (
    <Panel title="المستودعات">
      <p className="mb-3 text-xs text-ink-soft">
        المستودعُ المعطَّلُ لا يظهر في شاشات الإدخال والتسوية والتحويل،
        <b> ويبقى كلُّ ما تحرّك فيه في الدفاتر</b>. وتفعيلُه يُعيده متى احتجتَه.
      </p>

      {active.length === 1 && (
        <Banner tone="ok">
          مستودعٌ واحدٌ فعّالٌ: «{active[0]!.nameAr}» — ويُختار سلفًا في كلّ شاشة،
          فلا تُسأل عنه.
        </Banner>
      )}

      {warehouses.isPending && <Spinner />}
      {warehouses.isError && <ErrorBanner error={warehouses.error} />}

      <ul className="mt-3 space-y-2">
        {rows.map((warehouse) => (
          <li key={warehouse.id}>
            <Card warehouse={warehouse} mayEdit={mayEdit} />
          </li>
        ))}
      </ul>
    </Panel>
  )
}

function Card({ warehouse, mayEdit }: { warehouse: Warehouse; mayEdit: boolean }) {
  const queries = useQueryClient()
  const [asking, setAsking] = useState(false)
  const [reason, setReason] = useState('')

  const toggle = useMutation({
    mutationFn: (archived: boolean) =>
      api<Warehouse>(`/api/v1/warehouses/${warehouse.id}/archive`, {
        method: 'POST',
        body: { archived, reason: archived ? reason.trim() : null },
      }),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: ['warehouses'] })
      setAsking(false)
      setReason('')
    },
  })

  const locked = SYSTEM_KINDS[warehouse.kind]

  return (
    <div
      className={
        'rounded-md border px-3 py-2 '
        + (warehouse.isActive ? 'border-line' : 'border-line bg-surface opacity-70')
      }
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-medium">
          {warehouse.nameAr}
          {!warehouse.isActive && <span className="mr-2 text-xs text-warn">معطَّل</span>}
        </span>
        <span className="tabular text-xs text-ink-soft" dir="ltr">
          {warehouse.code}
        </span>
      </div>

      <div className="mt-1 text-xs text-ink-soft">
        {KIND_NAMES[warehouse.kind] ?? warehouse.kind}
        {locked && <span className="block text-warn">لا يُعطَّل: {locked}</span>}
      </div>

      {mayEdit && !locked && (
        <div className="mt-2">
          {!warehouse.isActive ? (
            <Button disabled={toggle.isPending} onClick={() => toggle.mutate(false)}>
              {toggle.isPending ? 'يُفعَّل…' : 'تفعيل'}
            </Button>
          ) : !asking ? (
            <Button onClick={() => setAsking(true)}>تعطيل</Button>
          ) : (
            <div className="space-y-2">
              <Field label="سببُ التعطيل" hint="يبقى في الدفتر — «لا نستعمله في هذه المرحلة» تُجيب.">
                <TextInput value={reason} onChange={setReason} />
              </Field>
              <div className="flex gap-2">
                <Button
                  tone="danger"
                  disabled={toggle.isPending || reason.trim().length === 0}
                  onClick={() => toggle.mutate(true)}
                >
                  {toggle.isPending ? 'يُعطَّل…' : 'تأكيدُ التعطيل'}
                </Button>
                <Button onClick={() => setAsking(false)}>تراجع</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {toggle.isError && (
        <div className="mt-2">
          <ErrorBanner error={toggle.error} />
        </div>
      )}
    </div>
  )
}
